import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Empty } from "antd";
import JobApplicationinterviewRoundsHeader from "./JobApplicationinterviewRoundsHeader";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import { Session } from "../../../../Entities/Session";
import { JobApplication } from "../../../../Entities/JobApplication";
import JobApplicationinterviewRoundsList from "./JobApplicationinterviewRoundsList";
import JobApplicationinterviewRoundsDetails from "./JobApplicationinterviewRoundsDetails";
import JobApplicationinterviewRoundsViolationModal from "./JobApplicationinterviewRoundsViolationModal";
import JobApplicationinterviewRoundsScreenRecordingModal from "./JobApplicationinterviewRoundsScreenRecordingModal";
import AssessmentRoundCard from "./AssessmentRoundCard";
import useAssessmentStatistics, { RoundStatistics } from "./useAssessmentStatistics";
import { RoundStatusFilter } from "./AssessmentStatusFilter";
import "./JobApplicationInterviewRounds.scss";

interface JobApplicationinterviewRoundsProps {
  interviewRounds: InterviewRound[];
  interviewSessions: Session[];
  jobPost: JobDescriptionEntity;
  selectedSession: Session | null;
  setSelectedSession: React.Dispatch<React.SetStateAction<Session | null>>;
  jobApplication: JobApplication | null;
  setJobApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
  setInterviewSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  setActiveTabKey: (key: string) => void;
  selectedRoundId?: string | null; // For navigation from Summary tab
}

const JobApplicationinterviewRounds: React.FC<
  JobApplicationinterviewRoundsProps
> = ({
  interviewRounds,
  interviewSessions,
  jobPost,
  selectedSession,
  setSelectedSession,
  jobApplication,
  setJobApplication,
  setInterviewSessions,
  setActiveTabKey,
  selectedRoundId,
}) => {
  const [showViolationModal, setShowViolationModal] = useState(false);
  const [showScreenRecordingModal, setShowScreenRecordingModal] =
    useState(false);
  const [questionFilter, setQuestionFilter] = useState<
    "all" | "qualified" | "unqualified" | "pending"
  >("all");
  const [roundFilter, setRoundFilter] = useState<RoundStatusFilter>("all");
  const [selectedRoundIdState, setSelectedRoundIdState] = useState<string | null>(null);
  const [filterCounts, setFilterCounts] = useState({
    all: 0,
    qualified: 0,
    unqualified: 0,
    pending: 0,
  });

  // Prepare data for useAssessmentStatistics hook — exclude profile-match (shown on Summary tab)
  const jobPostInterviewRounds = useMemo(
    () =>
      jobPost.interviewRounds
        .filter((r) => r.interviewRoundId !== null && r.interviewRoundId !== "profile-match")
        .map((r) => ({
          interviewRoundId: r.interviewRoundId as string,
          interviewRoundNumber: r.interviewRoundNumber,
        }))
        .sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber),
    [jobPost.interviewRounds]
  );

  // Exclude profile-match sessions from the Assessments tab (handled on Summary tab)
  const assessmentSessions = useMemo(
    () => interviewSessions.filter((s) => s.interviewRoundId !== "profile-match"),
    [interviewSessions]
  );

  // Get statistics for rounds with sessions
  const { statistics, loading: statsLoading } = useAssessmentStatistics({
    interviewSessions: assessmentSessions,
    interviewRounds,
    jobPostInterviewRounds,
  });

  // Track previous interviewSessions to detect application changes
  const previousSessionsRef = useRef<string | null>(null);

  // Reset round filter when a new application is selected (interviewSessions changes)
  useEffect(() => {
    const currentFirstSessionId =
      interviewSessions.length > 0 ? interviewSessions[0]._id : null;
    const previousFirstSessionId = previousSessionsRef.current;

    // If the first session ID changed, it means a new application was selected
    if (
      previousFirstSessionId !== null &&
      currentFirstSessionId !== previousFirstSessionId
    ) {
      setRoundFilter("all");
    }

    // Update the ref for next comparison
    previousSessionsRef.current = currentFirstSessionId;
  }, [interviewSessions]);

  // Reset question filter when selected session changes
  useEffect(() => {
    setQuestionFilter("all");
  }, [selectedSession?._id]);

  // Build unified rounds data (similar to Summary tab)
  const unifiedRounds = useMemo(() => {
    if (!jobPost.interviewRounds || jobPost.interviewRounds.length === 0) {
      return [];
    }

    const configuredRounds = jobPost.interviewRounds
      .filter((r) => r.interviewRoundId !== null && r.interviewRoundId !== "profile-match")
      .sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber);

    // Group sessions by interviewRoundId (exclude profile-match — shown on Summary tab)
    const sessionsByRoundId: Record<string, Session[]> = {};
    assessmentSessions.forEach((session) => {
      if (!session || !session._id) return;
      const roundId = session.interviewRoundId;
      if (!sessionsByRoundId[roundId]) {
        sessionsByRoundId[roundId] = [];
      }
      sessionsByRoundId[roundId].push(session);
    });

    // Sort sessions within each round by createdAt
    Object.keys(sessionsByRoundId).forEach((roundId) => {
      sessionsByRoundId[roundId].sort((a, b) => {
        const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        if (timeDiff !== 0) return timeDiff;
        return (a._id || '').localeCompare(b._id || '');
      });
    });

    // Build unified round data
    return configuredRounds.map((configRound, index) => {
      const roundId = configRound.interviewRoundId as string;
      const roundSessions = sessionsByRoundId[roundId] || [];
      const latestSession = roundSessions.length > 0 
        ? roundSessions[roundSessions.length - 1] 
        : null;

      // Find round details from interviewRounds.
      // Special round IDs ("profile-match", "overall-assessment") are stored as interviewRoundId
      // in the job post config but the actual InterviewRound documents from getRounds have MongoDB
      // _ids, not these special strings. Fall back to matching by title/category for special rounds.
      const roundDetailsBySpecialId =
        roundId === "profile-match"
          ? interviewRounds.find(
              (r) =>
                r.title?.toLowerCase().trim() === "profile matching" ||
                (r.category || []).some(
                  (cat) => cat.toLowerCase().replace(/\s+/g, "") === "profilematch"
                )
            )
          : roundId === "overall-assessment"
          ? interviewRounds.find(
              (r) => r.title?.toLowerCase().trim() === "overall assessment"
            )
          : undefined;
      const roundDetails = interviewRounds.find((r) => r._id === roundId) ?? roundDetailsBySpecialId;

      // Known display names for special rounds when no API document is matched
      const specialRoundTitle =
        roundId === "profile-match"
          ? "Profile Matching"
          : roundId === "overall-assessment"
          ? "Overall Assessment"
          : undefined;

      // Determine status
      let status: 'not-attempted' | 'pending' | 'qualified' | 'unqualified' = 'not-attempted';
      if (latestSession) {
        status = latestSession.status === 'qualified' 
          ? 'qualified' 
          : latestSession.status === 'unqualified' 
          ? 'unqualified' 
          : 'pending';
      }

      // displayRoundNumber is 1-based position in the filtered (non-profile-match) list.
      const displayRoundNumber = index + 1;
      return {
        roundId,
        roundNumber: configRound.interviewRoundNumber,
        title: roundDetails?.title || specialRoundTitle || `Round ${displayRoundNumber}`,
        displayRoundNumber,
        isMustQualify: configRound.isMustQualify || false,
        passPercentage: configRound.passPercentage,
        sessions: roundSessions,
        latestSession,
        status,
        hasAttempted: roundSessions.length > 0,
      };
    });
  }, [
    jobPost.interviewRounds,
    assessmentSessions,
    interviewRounds,
  ]);

  // Initialize selected round - prioritize sessionStorage (navigation from Summary), then selectedSession's round, then first round
  useEffect(() => {
    if (unifiedRounds.length === 0) return;

    // Check sessionStorage first (for navigation from Summary tab)
    const storedRoundId = sessionStorage.getItem('selectedRoundId');
    const storedSessionId = sessionStorage.getItem('selectedSessionId');
    if (storedRoundId && unifiedRounds.some(r => r.roundId === storedRoundId)) {
      setSelectedRoundIdState(storedRoundId);
      const round = unifiedRounds.find(r => r.roundId === storedRoundId);
      // Prefer the session from Summary: use storedSessionId if it belongs to this round, else parent's selectedSession for this round, else latest
      const sessionFromStorage =
        storedSessionId &&
        interviewSessions.find(
          (s) => s._id === storedSessionId && s.interviewRoundId === storedRoundId
        );
      const sessionFromParent =
        selectedSession?.interviewRoundId === storedRoundId ? selectedSession : null;
      const sessionToSelect =
        sessionFromStorage ?? sessionFromParent ?? round?.latestSession ?? null;
      if (sessionToSelect) {
        setSelectedSession(sessionToSelect);
      }
      // Clear sessionStorage after using it
      sessionStorage.removeItem('selectedRoundId');
      sessionStorage.removeItem('selectedSessionId');
      return;
    }

    // If selectedSession exists, use its round
    if (selectedSession?.interviewRoundId && unifiedRounds.some(r => r.roundId === selectedSession.interviewRoundId)) {
      if (selectedRoundIdState !== selectedSession.interviewRoundId) {
        setSelectedRoundIdState(selectedSession.interviewRoundId);
      }
      return;
    }

    // Default to first round if no selection exists (when tab is opened directly)
    // This ensures the first available round is always selected when Assessments tab is opened
    if (!selectedRoundIdState && unifiedRounds.length > 0) {
      const firstRound = unifiedRounds[0];
      setSelectedRoundIdState(firstRound.roundId);
      // Select the first round's session if available, otherwise just select the round
      if (firstRound.latestSession) {
        setSelectedSession(firstRound.latestSession);
      } else {
        // Even if no session, still select the round so it's highlighted
        setSelectedSession(null);
      }
    }
  }, [unifiedRounds, selectedSession, setSelectedSession, interviewSessions]);

  // Handle round selection. When sessionId is provided (card/dropdown click), the list already set selectedSession - only update round id.
  // When sessionId is not provided, set session to round.latestSession (e.g. initial sync).
  const handleRoundSelect = useCallback((roundId: string, sessionId?: string) => {
    setSelectedRoundIdState(roundId);
    if (sessionId != null) {
      // List already called setSelectedSession(session); do not overwrite.
      return;
    }
    const round = unifiedRounds.find(r => r.roundId === roundId);
    if (round?.latestSession) {
      setSelectedSession(round.latestSession);
    } else {
      setSelectedSession(null);
    }
  }, [unifiedRounds, setSelectedSession]);

  const isInterviewRoundsAvailable =
    interviewRounds.length > 0 && jobPost.interviewRounds.length > 0;

  // Use assessment-only sessions (profile-match excluded) to determine if any assessment has been generated
  const isAtleastOneRoundGenerated =
    isInterviewRoundsAvailable && assessmentSessions.length > 0;

  const handleViolationModal = () => {
    setShowViolationModal(!showViolationModal);
  };

  const handleScreenRecordingModal = () => {
    setShowScreenRecordingModal(!showScreenRecordingModal);
  };

  const handleStatisticsLoaded = useCallback((stats: typeof filterCounts) => {
    setFilterCounts(stats);
  }, []);

  // Calculate attempt info for the selected session
  const selectedSessionAttemptInfo = useMemo(() => {
    if (!selectedSession || !assessmentSessions.length) {
      return { attemptNumber: 1, isRestarted: false };
    }

    // Group assessment sessions by interviewRoundId
    const sessionsByRound: Record<string, Session[]> = {};
    assessmentSessions.forEach((session) => {
      const roundId = session.interviewRoundId;
      if (!sessionsByRound[roundId]) {
        sessionsByRound[roundId] = [];
      }
      sessionsByRound[roundId].push(session);
    });

    // Sort sessions by createdAt
    const roundSessions = sessionsByRound[selectedSession.interviewRoundId] || [];
    roundSessions.sort((a, b) => {
      const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (timeDiff !== 0) return timeDiff;
      return (a._id || '').localeCompare(b._id || '');
    });

    const attemptNumber = roundSessions.findIndex((s) => s._id === selectedSession._id) + 1;
    const isRestarted = roundSessions.length > 1;

    return { attemptNumber, isRestarted };
  }, [selectedSession, assessmentSessions]);

  return (
    <div className="job-application-interview-rounds-container">
      {isInterviewRoundsAvailable && isAtleastOneRoundGenerated && (
        <JobApplicationinterviewRoundsHeader
          roundFilter={roundFilter}
          setRoundFilter={setRoundFilter}
          filterCounts={filterCounts}
          jobApplication={jobApplication}
          setJobApplication={setJobApplication}
          selectedSession={selectedSession}
          interviewSessions={interviewSessions}
          setInterviewSessions={setInterviewSessions}
          setActiveTabKey={setActiveTabKey}
        />
      )}

      {isInterviewRoundsAvailable && (
        <div className="job-application-interview-rounds-content">
          <div className="job-application-interview-rounds-listing">
            <JobApplicationinterviewRoundsList
              selectedSession={selectedSession}
              setSelectedSession={setSelectedSession}
              interviewSessions={assessmentSessions}
              interviewRounds={interviewRounds}
              jobPost={jobPost}
              roundFilter={roundFilter}
              questionFilter={questionFilter}
              setQuestionFilter={setQuestionFilter}
              onStatisticsLoaded={handleStatisticsLoaded}
              unifiedRounds={unifiedRounds}
              selectedRoundId={selectedRoundIdState}
              onRoundSelect={handleRoundSelect}
            />
          </div>
          <div className="job-application-interview-rounds-details">
            {selectedSession && (
              <JobApplicationinterviewRoundsDetails
                selectedSession={selectedSession}
                interviewRounds={interviewRounds}
                jobPost={jobPost}
                handleViolationModal={handleViolationModal}
                handleScreenRecordingModal={handleScreenRecordingModal}
                questionFilter={questionFilter}
                setQuestionFilter={setQuestionFilter}
                passPercentage={
                  jobPost.interviewRounds.find(
                    (round) =>
                      round.interviewRoundId ===
                      selectedSession.interviewRoundId
                  )?.passPercentage || 0
                }
                isMustQualify={
                  jobPost.interviewRounds.find(
                    (round) =>
                      round.interviewRoundId ===
                      selectedSession.interviewRoundId
                  )?.isMustQualify || false
                }
                attemptNumber={selectedSessionAttemptInfo.attemptNumber}
                isRestarted={selectedSessionAttemptInfo.isRestarted}
              />
            )}
          </div>
        </div>
      )}

      {!isInterviewRoundsAvailable && !isAtleastOneRoundGenerated && (
        <div className="job-application-details-empty-placeholder-container">
          <Empty
            description="No assessments are available for this job post."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}

      {isInterviewRoundsAvailable && !isAtleastOneRoundGenerated && (
        <div className="job-application-details-empty-placeholder-container">
          <Empty
            description="No assessments have been generated for this job application."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}

      {selectedSession && (
        <JobApplicationinterviewRoundsViolationModal
          show={showViolationModal}
          onClose={handleViolationModal}
          selectedSession={selectedSession}
          roundName={
            interviewRounds.find(
              (round) => round._id === selectedSession.interviewRoundId
            )?.title || ""
          }
        />
      )}
      {selectedSession && (
        <JobApplicationinterviewRoundsScreenRecordingModal
          show={showScreenRecordingModal}
          onClose={handleScreenRecordingModal}
          selectedSession={selectedSession}
          roundName={
            interviewRounds.find(
              (round) => round._id === selectedSession.interviewRoundId
            )?.title || ""
          }
        />
      )}
    </div>
  );
};

export default JobApplicationinterviewRounds;
